import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import {
  FaHandshake,
  FaExternalLinkAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import { getAllPartenaires } from "../../services/partenairesServices";
import Reveal from "../../components/Reveal/Reveal";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const FALLBACK = "/img/no-image.jpg";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function toAbsolute(url) {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  // Images publiques front : /img/...
  if (cleanUrl.startsWith("/img/")) {
    return cleanUrl;
  }

  // Uploads backend : /uploads/...
  if (cleanUrl.startsWith("/uploads")) {
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${baseUrl}${cleanUrl}`;
  }

  return cleanUrl;
}

const EmptyState = () => {
  return (
    <div className="rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-primary dark:bg-orange-500/10">
        <FaHandshake />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucun partenaire publié
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
        Les partenaires du club apparaîtront ici dès qu’ils seront ajoutés dans
        l’administration.
      </p>
    </div>
  );
};

const ErrorState = ({ message }) => {
  return (
    <div className="rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-center text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
};

const PartnerCard = ({ partenaire, onImgError }) => {
  const src = toAbsolute(partenaire?.photo_url) || FALLBACK;

  const isInternal = Boolean(partenaire?.is_detail);
  const isExternal = !isInternal && Boolean(partenaire?.link_url);

  const card = (
    <article
      className={cn(
        "group relative flex h-full min-h-[210px] flex-col overflow-hidden rounded-[1.7rem]",
        "border border-slate-200 bg-white p-4 shadow-sm",
        "transition duration-300 hover:-translate-y-1.5 hover:shadow-xl",
        "dark:border-white/10 dark:bg-slate-900"
      )}
    >
      {(isExternal || isInternal) && (
        <div className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-700 shadow-lg backdrop-blur-xl transition group-hover:border-primary/30 group-hover:text-primary dark:border-white/10 dark:bg-slate-950/80 dark:text-slate-200">
          {isExternal ? (
            <FaExternalLinkAlt className="text-xs" />
          ) : (
            <FaHandshake className="text-sm" />
          )}
        </div>
      )}

      <div className="flex flex-1 items-center justify-center rounded-[1.35rem] border border-slate-200 bg-slate-50 px-5 py-6 dark:border-white/10 dark:bg-white/5">
        <img
          src={src}
          alt={partenaire?.nom || "Partenaire"}
          loading="lazy"
          decoding="async"
          onError={onImgError}
          className="max-h-24 w-full object-contain transition duration-300 group-hover:scale-105"
        />
      </div>

      <div className="px-2 pb-2 pt-4 text-center">
        <h3 className="line-clamp-2 font-barlow text-base font-extrabold leading-tight text-slate-950 dark:text-white">
          {partenaire?.nom || "Partenaire"}
        </h3>

        {(isExternal || isInternal) && (
          <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
            {isInternal ? "Voir la fiche partenaire" : "Ouvrir le site"}
          </p>
        )}
      </div>

      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80 transition group-hover:opacity-100"
      />
    </article>
  );

  if (isInternal) {
    return (
      <Link
        to={`/partenaires/${encodeURIComponent(partenaire.nom)}`}
        className="block h-full focus:outline-none"
        aria-label={`Voir le partenaire ${partenaire.nom}`}
      >
        {card}
      </Link>
    );
  }

  if (isExternal) {
    return (
      <a
        href={partenaire.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block h-full focus:outline-none"
        aria-label={`Ouvrir le site du partenaire ${partenaire.nom}`}
      >
        {card}
      </a>
    );
  }

  return <div className="block h-full">{card}</div>;
};

const PartnerSkeleton = () => {
  return (
    <div className="rounded-[1.7rem] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="h-32 animate-pulse rounded-[1.35rem] bg-slate-200 dark:bg-slate-800" />
      <div className="mx-auto mt-4 h-4 w-28 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  );
};

export default function PartenairesCarousel() {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const sliderRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    const fetchPartenaires = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const all = await getAllPartenaires();

        const active = (Array.isArray(all) ? all : []).filter((partenaire) => {
          if (
            partenaire?.is_active === undefined ||
            partenaire?.is_active === null ||
            partenaire?.is_active === ""
          ) {
            return true;
          }

          return (
            partenaire.is_active === true ||
            partenaire.is_active === 1 ||
            partenaire.is_active === "1"
          );
        });

        if (!mounted) return;

        setPartenaires(active);
      } catch (error) {
        console.error("Erreur lors de la récupération des partenaires :", error);

        if (mounted) {
          setErrorMsg("Impossible de charger les partenaires.");
          setPartenaires([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPartenaires();

    return () => {
      mounted = false;
    };
  }, []);

  const onImgError = (event) => {
    if (event?.currentTarget) {
      event.currentTarget.src = FALLBACK;
    }
  };

  const count = partenaires.length;
  const safeCount = Math.max(1, count);

  const desktopShow = Math.min(5, safeCount);
  const laptopShow = Math.min(4, safeCount);
  const tabletShow = Math.min(3, safeCount);
  const mobileShow = Math.min(2, safeCount);

  const canLoop = count > desktopShow;
  const canAutoPlay = count > desktopShow;

  const settings = useMemo(
    () => ({
      infinite: canLoop,
      slidesToShow: desktopShow,
      slidesToScroll: 1,
      autoplay: canAutoPlay,
      autoplaySpeed: 2600,
      speed: 850,
      cssEase: "ease-in-out",
      arrows: false,
      dots: count > desktopShow,
      pauseOnHover: true,
      pauseOnFocus: true,
      swipeToSlide: true,
      responsive: [
        {
          breakpoint: 1280,
          settings: {
            slidesToShow: laptopShow,
            dots: count > laptopShow,
            infinite: count > laptopShow,
            autoplay: count > laptopShow,
          },
        },
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: tabletShow,
            dots: count > tabletShow,
            infinite: count > tabletShow,
            autoplay: count > tabletShow,
          },
        },
        {
          breakpoint: 640,
          settings: {
            slidesToShow: mobileShow,
            dots: count > mobileShow,
            infinite: count > mobileShow,
            autoplay: count > mobileShow,
          },
        },
      ],
    }),
    [
      canLoop,
      canAutoPlay,
      desktopShow,
      laptopShow,
      tabletShow,
      mobileShow,
      count,
    ]
  );

  return (
    <section
      id="partenaires"
      className="relative scroll-mt-[calc(var(--site-navbar-h)+24px)] overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28"
    >
      <style>{`
        .partners-carousel .slick-track {
          display: flex !important;
          align-items: stretch;
        }

        .partners-carousel .slick-slide {
          height: auto;
        }

        .partners-carousel .slick-slide > div {
          height: 100%;
        }

        .partners-carousel .slick-dots {
          bottom: -38px;
        }

        .partners-carousel .slick-dots li button:before {
          font-size: 10px;
          opacity: .25;
          color: #f97316;
        }

        .partners-carousel .slick-dots li.slick-active button:before {
          opacity: .8;
          color: #f97316;
        }

        @media (prefers-reduced-motion: reduce) {
          .partners-carousel .slick-track {
            transition: none !important;
          }
        }
      `}</style>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaHandshake />
            Réseau du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Nos{" "}
            <span className="text-primary">partenaires</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Ils accompagnent et soutiennent le club dans son développement.
            Merci à eux pour leur confiance.
          </p>
        </Reveal>

        <div className="mt-12">
          {loading && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {Array.from({ length: 5 }).map((_, index) => (
                <PartnerSkeleton key={index} />
              ))}
            </div>
          )}

          {!loading && errorMsg && <ErrorState message={errorMsg} />}

          {!loading && !errorMsg && count === 0 && <EmptyState />}

          {!loading && !errorMsg && count > 0 && (
            <Reveal variant="zoom">
              <div className="relative rounded-[2.5rem] border border-slate-200 bg-white/80 p-5 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/80 sm:p-7">
                {count > 1 && (
                  <div className="mb-5 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => sliderRef.current?.slickPrev?.()}
                      className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                      aria-label="Partenaire précédent"
                      title="Précédent"
                    >
                      <FiChevronLeft className="text-xl" />
                    </button>

                    <button
                      type="button"
                      onClick={() => sliderRef.current?.slickNext?.()}
                      className="grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:text-primary dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
                      aria-label="Partenaire suivant"
                      title="Suivant"
                    >
                      <FiChevronRight className="text-xl" />
                    </button>
                  </div>
                )}

                {count === 1 ? (
                  <div className="mx-auto max-w-xs">
                    <PartnerCard
                      partenaire={partenaires[0]}
                      onImgError={onImgError}
                    />
                  </div>
                ) : (
                  <div className="partners-carousel pb-8">
                    <Slider ref={sliderRef} {...settings}>
                      {partenaires.map((partenaire) => (
                        <div key={partenaire.id} className="h-full px-2 py-2">
                          <PartnerCard
                            partenaire={partenaire}
                            onImgError={onImgError}
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}